import buildReportFilename from './reportFilename';

const toCamelCase = (name) => {
  if (!name) return name;
  if ([...name].every(c => (c >= 'A' && c <= 'Z') || c === '_')) return name.toLowerCase();
  return name.charAt(0).toLowerCase() + name.slice(1);
};

/**
 * Generate and download a CSV report.
 */
const exportReportCsv = async ({
  config,
  columns,
  data,
  groupByField,
  autoDownload = false,
  isCancelled,
  onProgress
}) => {
  try {
    if (onProgress) onProgress('Generating CSV...');

    const escapeCell = (val) => {
      if (val == null) return '';
      const str = String(val);
      // Escape if contains comma, quote, or newline
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const lines = [];
    const headerRow = columns.map(col => escapeCell(col.label)).join(',');
    lines.push(headerRow);

    if (groupByField) {
      // Group rows with a separator header between groups
      const groupMap = new Map();
      data.forEach(row => {
        const cf = toCamelCase(groupByField);
        const val = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
        const key = val != null ? String(val) : '(Blank)';
        if (!groupMap.has(key)) groupMap.set(key, []);
        groupMap.get(key).push(row);
      });
      let first = true;
      [...groupMap.entries()]
        .sort(([a], [b]) => {
          if (a === '(Blank)') return 1;
          if (b === '(Blank)') return -1;
          return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
        })
        .forEach(([groupVal, groupRows]) => {
        if (!first) lines.push('');
        lines.push(escapeCell(`--- ${groupVal} ---`));
        groupRows.forEach(row => {
          lines.push(columns.map(col => {
            const camelField = toCamelCase(col.field);
            const displayKey = `${camelField}_Display`;
            return escapeCell(row[displayKey] != null ? row[displayKey] : row[camelField]);
          }).join(','));
        });
        first = false;
      });
    } else {
      data.forEach(row => {
        lines.push(columns.map(col => {
          const camelField = toCamelCase(col.field);
          const displayKey = `${camelField}_Display`;
          return escapeCell(row[displayKey] != null ? row[displayKey] : row[camelField]);
        }).join(','));
      });
    }

    const csvContent = lines.join('\r\n');

    if (isCancelled && isCancelled()) { if (onProgress) onProgress(null); return false; }

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' }); // BOM for Excel compat

    const filename = buildReportFilename(config.reportName, 'csv');

    if (!autoDownload && window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{ description: 'CSV File', accept: { 'text/csv': ['.csv'] } }]
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        if (onProgress) onProgress(null);
        return true;
      } catch (err) {
        if (err.name === 'AbortError') { if (onProgress) onProgress(null); return false; }
      }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    if (onProgress) onProgress(null);
    return true;
  } catch (err) {
    if (onProgress) onProgress(null);
    throw err;
  }
};

export default exportReportCsv;
